import React, { useCallback, useContext, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import {
    loginThenComeBack as loginThenComeBackThunk,
    trackV2,
} from '@gumtree/shell/src/common/common-actions';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { stickyScroll } from './sticky-scroll';
import revealPhoneUtil from '../seller-contact-block/reveal-phone-util';
import type { VipState, VipUseDispatch } from '../../reducers';
import ContactButtons from '../contact-buttons/contact-buttons';
import MakeOffer from '../seller-contact-block/make-offer/make-offer';
import {
    H2Large,
    H2Small,
    StickyButtonsContainer,
    StickyHeaderContainer,
    StickyHeaderFnRow,
    StickyHeaderTxtRow,
    PostingLength,
} from './style';
import SellerPhone from '../seller-phone/app';
import { RatingsView } from '../seller-avatar-block';
import { MCUFeedContext, RequestAQuoteFormContext } from '../../vip-context';

const StickyHeader: React.FC = () => {
    const {
        sellerContactDetails,
        sellerRating,
        adDetails,
        baseConfig,
        sellerActions: { makeAnOfferEnabled },
    } = useSelector(selectState, shallowEqual);

    const { adSource } = useContext(RequestAQuoteFormContext);

    const {
        contactName,
        userLoggedIn,
        replyPhone,
        replyEmail,
        replyLink,
        revealedNumber,
        replySmsEnabled,
        showRevealNumber,
        isLoginRequiredToContactForVipCategory,
        revealNumberUnmasked,
        revealPhoneCallbackUrl,
    } = sellerContactDetails;
    const { postingSince, pro, l1CategorySeoName, containsCallTrackingFeature } = adDetails;
    const isForSale = l1CategorySeoName === 'for-sale';
    const isJobs = l1CategorySeoName === 'jobs';
    const isMobile = baseConfig.device.type === 'mobile';
    const isServices = l1CategorySeoName === 'business-services';

    const isMCUFeed = useContext(MCUFeedContext)?.isMCUFeed;
    const hideSMS = useFeatureIsOn(GrowthBookFeature.HIDE_SMS) && adDetails.isFromMotorsFeed;
    const isUI_202506 = useFeatureValue(GrowthBookFeature.SERVICE_NEW_UI, 'A') === 'B' && isServices;

    useEffect(() => {
        const cleanup = stickyScroll(isMobile, isMCUFeed, isUI_202506);
        return cleanup;
    }, [isMobile, isMCUFeed, isUI_202506]);

    const dispatch = useDispatch() as VipUseDispatch;
    const loginThenComeBack = useCallback(() => dispatch(loginThenComeBackThunk()), []);

    const revealPhone = (e) => {
        trackV2('R2SPhoneBegin');
        revealPhoneUtil(e, dispatch, sellerContactDetails, adDetails, baseConfig);
    };

    const handleRevealPhone = (e) => {
        e.preventDefault();
        revealPhone(e);
    };

    return (
        <StickyHeaderContainer
            data-q="sticky-banner"
            className={cx({ 'bottom-sticky': (isMCUFeed && isMobile) || isUI_202506, 'is-shown': isUI_202506 && isMobile})}
        >
            {!((isMCUFeed || isUI_202506) && isMobile) && (
                <StickyHeaderTxtRow>
                    <H2Small data-q="seller-name-small-screen">Contact {contactName}</H2Small>
                    <H2Large data-q="seller-name-large-screen">{contactName}</H2Large>
                    {isForSale && sellerRating?.total !== 0 && (
                        <RatingsView average={sellerRating?.average} total={sellerRating?.total} />
                    )}
                    {!isJobs && (
                        <PostingLength
                            hasRating={isForSale && sellerRating && sellerRating.total !== 0}
                        >
                            Posting for {postingSince}
                        </PostingLength>
                    )}
                </StickyHeaderTxtRow>
            )}
            <StickyHeaderFnRow pro={pro && containsCallTrackingFeature} isUI_202506={isUI_202506} >
                {!isMobile && replyPhone && (
                    <SellerPhone
                        isUserLoggedIn={userLoggedIn}
                        replyPhone={replyPhone}
                        revealedNumber={revealedNumber}
                        pro={pro}
                        containsCallTrackingFeature={containsCallTrackingFeature}
                        revealPhone={handleRevealPhone}
                        revealNumberUnmasked={revealNumberUnmasked}
                        showRevealNumber={showRevealNumber}
                        revealPhoneCallbackUrl={revealPhoneCallbackUrl}
                        isLoginRequiredToContactForVipCategory={
                            isLoginRequiredToContactForVipCategory
                        }
                        l1CategorySeoName={l1CategorySeoName}
                        isStickyHeader
                        isMCUFeed={isMCUFeed}
                        loginThenComeBack={loginThenComeBack}
                    />
                )}
                <StickyButtonsContainer
                    isMobile={isMobile}
                    canCall={isMobile && !!replyPhone}
                    canSms={isMobile && replySmsEnabled && !hideSMS}
                    canEmail={!!replyEmail}
                    canOffer={!!replyEmail && makeAnOfferEnabled}
                    isUI_202506={isUI_202506}
                    adSource={adSource || 'GT'}
                >
                    <ContactButtons
                        replyLink={replyLink}
                        replyEmail={replyEmail}
                        replyPhone={replyPhone}
                        replySmsEnabled={replySmsEnabled && !hideSMS}
                        revealPhoneUtil={revealPhoneUtil}
                        isStickyHeader
                        isMobile={isMobile}
                        l1CategorySeoName={l1CategorySeoName}
                    />
                    {!!replyEmail && makeAnOfferEnabled && adSource !== 'Bark' && <MakeOffer dropdown />}
                </StickyButtonsContainer>
            </StickyHeaderFnRow>
        </StickyHeaderContainer>
    );
};

function selectState({
    sellerContactDetails,
    sellerRating,
    adDetails,
    baseConfig,
    sellerActions,
}: VipState) {
    return {
        sellerContactDetails,
        sellerRating,
        adDetails,
        baseConfig,
        sellerActions,
    };
}

export default StickyHeader;
